import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useRef, type SubmitEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';
import { useFinpspDeposit } from '../../../mutations/finpsp';
import { createFinpspDepositSchema } from '../../../schemas/finpsp';
import { FINPSP_ENABLED_BANK_TYPES, FINPSP_DEPOSIT_TYPE, FINPSP_DEPOSIT_CHANNELS } from '../../../constants/finpsp';
import type { FinpspDepositRequest } from '../../../types/finpsp';
import useLocalization from '../../useLocalization';
import { useFinpspDepositTypes } from './useFinpspDepositTypes';
import { useOpenFinpspPayment } from './useOpenFinpspPayment';

export const useFinpspDepositForm = (paymentProvider: string | null) => {
  const { t } = useLocalization();
  const mutation = useFinpspDeposit();
  const depositTypes = useFinpspDepositTypes(paymentProvider);
  const openPayment = useOpenFinpspPayment();

  const submitting = useRef(false);
  const schema = useMemo(() => createFinpspDepositSchema(t), [t]);
  const form = useForm<z.input<typeof schema>, unknown, z.output<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { amount: '', type: depositTypes[0] ?? FINPSP_DEPOSIT_TYPE.P2A, customer_number: '' },
  });

  const selectedType = useWatch({ control: form.control, name: 'type' });
  const depositType = depositTypes.includes(selectedType) ? selectedType : (depositTypes[0] ?? FINPSP_DEPOSIT_TYPE.P2A);
  const channel = FINPSP_DEPOSIT_CHANNELS[depositType];
  const bankType = FINPSP_ENABLED_BANK_TYPES.find((method) => method === paymentProvider);
  const submit = (input: z.output<typeof schema>) => {
    if (submitting.current || !bankType || !depositTypes.includes(input.type)) {
      return;
    }

    submitting.current = true;

    const requestedChannel = FINPSP_DEPOSIT_CHANNELS[input.type];
    const request: FinpspDepositRequest = {
      amount: input.amount,
      type: input.type,
      bank_type: bankType,
    };

    if (requestedChannel.requiresCustomerNumber) {
      request.customer_number = input.customer_number;
    }

    if (requestedChannel.checkout === 'redirect' && window.location.protocol === 'https:') {
      request.redirect_url = new URL('/casino', window.location.origin).href;
    }

    mutation.mutate(request, {
      onSuccess: (response) => {
        if (!('authResults' in response) && requestedChannel.checkout === 'instructions') {
          openPayment(response.transactionId);
        }
      },
      onSettled: () => {
        submitting.current = false;
      },
    });
  };

  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    form.setValue('type', depositType);
    void form.handleSubmit(submit)(event);
  };

  return {
    form,
    depositType,
    depositTypes,
    requiresPhone: channel.requiresCustomerNumber,
    isSubmitting: mutation.isPending || form.formState.isSubmitting,
    onSubmit,
  };
};
